/**
 * Signal counter — Website Build Brief v2 §5.
 *
 * GET /api/counter -> { signals: n }, then:
 *   signals >= threshold  ->  show the number, count up to it
 *   signals <  threshold  ->  no number at all; the block reads
 *                             "Applications open · 50 founding seats"
 *
 * "Transparency creates tension only when the imbalance is favorable."
 *
 * The pre-threshold state is what ships in the HTML, so the page is never caught
 * showing a number it should be hiding — not even for a frame.
 */

import { config } from "./config.ts";

const DURATION_MS = 1400;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Count-up animation, lifted from the mock. Skipped under reduced-motion. */
function countUp(el: HTMLElement, target: number): void {
  if (prefersReducedMotion()) {
    el.textContent = target.toLocaleString();
    return;
  }
  const t0 = performance.now();
  const step = (now: number) => {
    const p = Math.min((now - t0) / DURATION_MS, 1);
    el.textContent = Math.round(easeOutCubic(p) * target).toLocaleString();
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function revealNumber(signals: number): void {
  document
    .querySelectorAll<HTMLElement>("[data-counter-signals]")
    .forEach(el => el.removeAttribute("hidden"));
  document
    .querySelectorAll<HTMLElement>("[data-counter-pre]")
    .forEach(el => el.setAttribute("hidden", ""));

  document
    .querySelectorAll<HTMLElement>("[data-count]")
    .forEach(el => countUp(el, signals));

  // The ticker carries the same figure; keep the two consistent.
  document.querySelectorAll<HTMLElement>("[data-ticker-seats]").forEach(el => {
    el.textContent = `FOUNDING 50 — ${signals.toLocaleString()} signals · ${config.foundingSeats} seats`;
  });
}

export async function initCounter(): Promise<void> {
  // No counter box on the page? Nothing to do.
  if (!document.querySelector("[data-counter-pre]")) return;

  try {
    const res = await fetch("/api/counter", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return; // stay in the pre-threshold state
    const data: unknown = await res.json();
    const raw =
      typeof data === "object" && data !== null && "signals" in data
        ? Number((data as { signals: unknown }).signals)
        : NaN;
    if (!Number.isFinite(raw)) return;

    const signals = Math.max(0, Math.trunc(raw)) + config.counterSeed;
    if (signals >= config.counterThreshold) revealNumber(signals);
  } catch {
    // Endpoint down: the page keeps the honest pre-threshold state rather than
    // inventing a number. Deliberate.
  }
}
